import { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import { useProject } from "@/features/project/project-context"
import type { HitTarget, KnowledgeSearch } from "./knowledge-model"

/**
 * Go where a ⌘K row or a search hit points (an item, goal or document page, a tab, or a sheet over the current page).
 */
export const useOpenTarget = () => {
  const { workspaceId, projectId } = useProject()
  const navigate = useNavigate()
  return useCallback(
    (target: HitTarget) => {
      switch (target._tag) {
        case "item":
          return void navigate({
            to: "/w/$workspaceId/p/$projectId/i/$ref",
            params: { workspaceId, projectId, ref: target.ref },
            search: target.search,
          })
        case "goal":
          return void navigate({
            to: "/w/$workspaceId/p/$projectId/g/$ref",
            params: { workspaceId, projectId, ref: target.ref },
            search: target.search,
          })
        case "document":
          return void navigate({
            to: "/w/$workspaceId/p/$projectId/o/$ref",
            params: { workspaceId, projectId, ref: target.ref },
            search: target.v !== undefined ? { v: target.v } : {},
          })
        case "memory":
          return void navigate({
            to: "/w/$workspaceId/p/$projectId/m/$ref",
            params: { workspaceId, projectId, ref: target.ref },
          })
        case "tab":
          return void navigate({
            to: "/w/$workspaceId/p/$projectId",
            params: { workspaceId, projectId },
            search: target.search,
          })
        case "over":
          return void navigate({
            to: ".",
            search: ((prev: Record<string, unknown>) => ({ ...prev, ...target.search })) as never,
            replace: true,
          })
      }
    },
    [navigate, workspaceId, projectId],
  )
}

/** Go to the search page (`/search?q=…`, `in=history` for past discussions and runs). */
export const useOpenSearchPage = () => {
  const { workspaceId, projectId } = useProject()
  const navigate = useNavigate()
  return useCallback(
    (search: KnowledgeSearch) =>
      void navigate({
        to: "/w/$workspaceId/p/$projectId/search",
        params: { workspaceId, projectId },
        search: search as never,
      }),
    [navigate, workspaceId, projectId],
  )
}
